import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { getApiUrl } from '../config';
import { GlassCard, GlassTextField, GlassButton, GlassInfoCard } from './ui/GlassComponents';

const TransactionLedger = ({ 
  open, 
  onClose, 
  entityType, // 'treasury', 'bank', or 'account'
  entityId, // nation name for treasury, bank_id for bank, account_number for account
  entityName,
  sessionData // Add sessionData prop for authentication
}) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    currency_symbol: '',
    transaction_type: '',
    from_date: '',
    to_date: '',
    limit: 50,
    offset: 0,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const formatEntityName = (name, type, id) => {
    if (name) {
      return name;
    }
    // Fallback if name resolution failed
    return type && id ? `${type} ${id}` : '-';
  };

  const fetchTransactions = async () => {
    if (!entityId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = sessionData?.token || localStorage.getItem('token');
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });
      
      const endpoint = entityType === 'treasury' 
        ? `/treasuries/${entityId}/transactions`
        : entityType === 'bank' 
          ? `/banks/${entityId}/transactions`
          : (() => {
              // For accounts: entityId format is "bankId|accountNumber"
              const [bankId, accountNumber] = entityId.split('|');
              return `/banks/${bankId}/accounts/${accountNumber}/transactions`;
            })();
      
      const response = await fetch(`${getApiUrl(endpoint)}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }
      
      const data = await response.json();
      setTransactions(data.transactions || []);
      setPagination(data.pagination || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionDetails = async (transactionId) => {
    try {
      const token = sessionData?.token || localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/transactions/${transactionId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch transaction details');
      }
      
      const data = await response.json();
      setSelectedTransaction(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (open && entityId) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      offset: 0, // Reset to first page when filtering
    }));
  };

  const handlePageChange = (event, newPage) => {
    setFilters(prev => ({
      ...prev,
      offset: (newPage - 1) * prev.limit,
    }));
  };

  const resetFilters = () => {
    setFilters({
      currency_symbol: '',
      transaction_type: '',
      from_date: '',
      to_date: '',
      limit: 50,
      offset: 0,
    });
  };

  const formatAmount = (amount, currencySymbol) => {
    return `${amount.toLocaleString()} ${currencySymbol}`;
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'mint': return 'success';
      case 'burn': return 'error';
      case 'transfer': return 'primary';
      case 'ubi': return 'info';
      case 'interest': return 'success';
      case 'maintenance_fee': return 'warning';
      case 'overdraft_fee': return 'error';
      default: return 'default';
    }
  };

  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 30, 40, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)',
            color: 'white',
          }
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle sx={{
          color: 'white',
          fontWeight: 600,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Transaction Ledger - {entityName}
            </Typography>
            <Box>
              <Tooltip title="Toggle Filters">
                <IconButton onClick={() => setShowFilters(!showFilters)} sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  <FilterIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchTransactions} sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
          {error && (
            <GlassInfoCard severity="error">
              {error}
            </GlassInfoCard>
          )}
          
          {showFilters && (
            <GlassCard sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.95)' }}>
                Filters
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <GlassTextField
                  label="Currency Symbol"
                  value={filters.currency_symbol}
                  onChange={(e) => handleFilterChange('currency_symbol', e.target.value)}
                  size="small"
                  sx={{ minWidth: 120 }}
                />
                <FormControl size="small" sx={{
                  minWidth: 120,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    background: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.08)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                    },
                    '&.Mui-focused': {
                      background: 'rgba(255, 255, 255, 0.08)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
                }}>
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    value={filters.transaction_type}
                    onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                    label="Transaction Type"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'rgba(30, 30, 40, 0.95)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '& .MuiMenuItem-root': {
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(255, 255, 255, 0.15)',
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="mint">Mint</MenuItem>
                    <MenuItem value="burn">Burn</MenuItem>
                    <MenuItem value="transfer">Transfer</MenuItem>
                    <MenuItem value="ubi">UBI</MenuItem>
                    <MenuItem value="interest">Interest</MenuItem>
                    <MenuItem value="maintenance_fee">Maintenance Fee</MenuItem>
                    <MenuItem value="overdraft_fee">Overdraft Fee</MenuItem>
                  </Select>
                </FormControl>
                <GlassTextField
                  label="From Date"
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => handleFilterChange('from_date', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <GlassTextField
                  label="To Date"
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => handleFilterChange('to_date', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl size="small" sx={{
                  minWidth: 80,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    background: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.08)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                    },
                    '&.Mui-focused': {
                      background: 'rgba(255, 255, 255, 0.08)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
                }}>
                  <InputLabel>Limit</InputLabel>
                  <Select
                    value={filters.limit}
                    onChange={(e) => handleFilterChange('limit', e.target.value)}
                    label="Limit"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'rgba(30, 30, 40, 0.95)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '& .MuiMenuItem-root': {
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(255, 255, 255, 0.15)',
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={200}>200</MenuItem>
                  </Select>
                </FormControl>
                <GlassButton onClick={resetFilters} variant="glass" size="small">
                  Reset
                </GlassButton>
              </Box>
            </GlassCard>
          )}
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                <Table>
                  <TableHead sx={{
                    '& .MuiTableCell-root': {
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }
                  }}>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Initiated By</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{
                    '& .MuiTableCell-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }
                  }}>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            No transactions found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((transaction) => (
                        <TableRow
                          key={transaction.id}
                          sx={{
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(transaction.created_at).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(transaction.created_at).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.transaction_type}
                              color={getTransactionTypeColor(transaction.transaction_type)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {transaction.currency_symbol}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatAmount(transaction.amount, transaction.currency_symbol)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={formatEntityName(transaction.from_name, transaction.from_type, transaction.from_id)}>
                              <Typography 
                                variant="body2"
                                sx={{
                                  maxWidth: 150,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'default'
                                }}
                              >
                                {formatEntityName(transaction.from_name, transaction.from_type, transaction.from_id)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={formatEntityName(transaction.to_name, transaction.to_type, transaction.to_id)}>
                              <Typography 
                                variant="body2"
                                sx={{
                                  maxWidth: 150,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'default'
                                }}
                              >
                                {formatEntityName(transaction.to_name, transaction.to_type, transaction.to_id)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={transaction.description || 'No description'}>
                              <Typography 
                                variant="body2"
                                sx={{
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'default'
                                }}
                              >
                                {transaction.description || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {transaction.initiated_by}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => fetchTransactionDetails(transaction.id)}
                                sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
              
              <Box mt={2}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Showing {transactions.length} of {pagination.total} transactions
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Transaction Details Dialog */}
      <Dialog
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 30, 40, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)',
            color: 'white',
          }
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle sx={{
          color: 'white',
          fontWeight: 600,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Transaction Details
            </Typography>
            <IconButton onClick={() => setSelectedTransaction(null)} sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
          {selectedTransaction && (
            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }} gutterBottom>
                Transaction ID: {selectedTransaction.id}
              </Typography>

              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>Type</Typography>
                <Chip
                  label={selectedTransaction.transactionType}
                  color={getTransactionTypeColor(selectedTransaction.transactionType)}
                  size="small"
                />
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>Amount</Typography>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {formatAmount(selectedTransaction.amount, selectedTransaction.currencySymbol)}
                </Typography>
              </Box>

              {selectedTransaction.from && (
                <Box mb={2}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>From</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {selectedTransaction.from.name} ({selectedTransaction.from.type})
                  </Typography>
                </Box>
              )}

              {selectedTransaction.to && (
                <Box mb={2}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>To</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {selectedTransaction.to.name} ({selectedTransaction.to.type})
                  </Typography>
                </Box>
              )}

              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>Initiated By</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {selectedTransaction.initiatedBy}
                </Typography>
              </Box>

              {selectedTransaction.description && (
                <Box mb={2}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>Description</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {selectedTransaction.description}
                  </Typography>
                </Box>
              )}

              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>Date</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {new Date(selectedTransaction.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransactionLedger; 